import { Button, Flex, ModalFooter, Stack, Text } from '@chakra-ui/react';
import { COLORS } from '../../constant';
import { ReactCrop } from 'react-image-crop';
import type { Crop } from 'react-image-crop';
import { useEffect, useRef, useState } from 'react';
import { ModalTemplate } from '../../connection-detail-v2/components/ModalTemplate';

export const CropImage = ({
  isOpen,
  onClose,
  onSave,
  base64Image,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (blob: Blob) => void;
  base64Image: string;
}) => {
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = useState<Crop>();
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string>('');
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  const cropImage = ({
    image,
    width,
    height,
    xstart,
    ystart,
    callback,
  }: {
    image: HTMLImageElement;
    width: number;
    height: number;
    xstart: number;
    ystart: number;
    callback: (blob: Blob | null) => void;
  }) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(image, xstart, ystart, width, height, 0, 0, width, height);
      canvas.toBlob((blob) => {
        callback(blob);
      }, 'image/png');
    } catch (e) {
      callback(null);
    }
  };

  const onCropComplete = (crop: Crop) => {
    if (!crop.width || !crop.height || !imageRef.current) return;

    const scaleX = dimensions.width / imageRef.current.width;
    const scaleY = dimensions.height / imageRef.current.height;

    cropImage({
      image: imageRef.current,
      xstart: crop.x * scaleX,
      ystart: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY,
      callback: (blob) => {
        setCroppedImageBlob(blob);
        if (blob) {
          setCroppedImageUrl(URL.createObjectURL(blob));
        } else {
          setCroppedImageUrl('');
        }
      },
    });
  };

  // Clean up object URL when modal closes or blob changes
  useEffect(() => {
    return () => {
      if (croppedImageUrl) {
        URL.revokeObjectURL(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  return (
    <ModalTemplate
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setCroppedImageBlob(null);
        setCroppedImageUrl('');
        setCrop(undefined);
      }}
      footer={
        <ModalFooter width={'100%'}>
          <Stack
            direction={'row'}
            width={'100%'}
            gap={4}
            justifyContent="flex-end"
          >
            <Button
              onClick={async () => {
                if (croppedImageBlob) {
                  onSave(croppedImageBlob);
                } else if (imageRef.current) {
                  // If no crop, convert the original image to a blob and save
                  const img = imageRef.current;
                  const canvas = document.createElement('canvas');
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    canvas.toBlob((blob) => {
                      if (blob) {
                        onSave(blob);
                      }
                    }, 'image/png');
                  }
                }
                setCroppedImageBlob(null);
                setCroppedImageUrl('');
                setCrop(undefined);
              }}
              variant="solid"
              background={COLORS.FINCH.BLACK}
              _hover={{
                background: COLORS.FINCH.BLACK,
              }}
              color={COLORS.WHITE}
            >
              Save Image
            </Button>
          </Stack>
        </ModalFooter>
      }
    >
      <Flex
        direction={'column'}
        gap={2}
        alignItems={'center'}
        width={'100%'}
        height={'100%'}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Text
            fontSize={'16px'}
            fontWeight={'800'}
            fontStyle={'bold'}
            position={'relative'}
            bottom={'35px'}
          >
            Add a profile photo
          </Text>
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => onCropComplete(c)}
            ruleOfThirds
          >
            <img
              ref={imageRef}
              onLoad={(e) => {
                setDimensions({
                  width: e.currentTarget.naturalWidth,
                  height: e.currentTarget.naturalHeight,
                });
              }}
              src={base64Image}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              alt=""
            />
          </ReactCrop>
        </div>
        <Flex
          dir="column"
          gap={2}
          display={'flex'}
          width={'100%'}
          alignItems={'flex-start'}
          height={'100%'}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '8px',
              height: '100%',
              width: '100%',
            }}
          >
            {croppedImageUrl && (
              <>
                <Text
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    height: 'fit-content',
                  }}
                >
                  Preview
                </Text>
                <img
                  src={croppedImageUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    border: '1px solid ' + COLORS.GRAY.GRAY_400,
                    borderRadius: '8px',
                  }}
                  alt=""
                />
              </>
            )}
          </div>
        </Flex>
      </Flex>
    </ModalTemplate>
  );
};
